import { useState } from "react";
import { Check, Sparkles, Copy, FileDown, Columns2 } from "lucide-react";
import { FinishCompare } from "@/components/viewer/FinishCompare";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, asset, money, errMsg, sessionId, TaxLines } from "@/lib/api";
import { BrandButton } from "@/components/LeadForms";

export function calcTotal(base, finishes, choices) {
  const up = finishes.reduce((s, c) => {
    const o = c.options.find((x) => x.id === choices[c.id]);
    return s + (o?.is_upgrade ? o.price : 0);
  }, 0);
  return { base, upgrades: up, total: base + up };
}

export function PersonaliseDrawer({ open, onOpenChange, finishes, choices, onPick, base, focusCat, onSave, builder }) {
  const t = calcTotal(base, finishes, choices);
  const [cmp, setCmp] = useState(null);
  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[480px] p-0 flex flex-col" data-testid="personalise-drawer">
        <SheetHeader className="p-6 pb-2 text-left">
          <SheetTitle className="font-serif text-3xl">Personalise</SheetTitle>
          <SheetDescription>Choose your finishes. Upgrades are added to your running total.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-7">
          {finishes.map((c) => (
            <section key={c.id} className={focusCat === c.id ? "rounded-2xl ring-2 ring-ba p-3 -m-3" : ""} data-testid={`finish-category-${c.id}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs uppercase tracking-[0.25em] text-slate-500">{c.name}</h4>
                {c.options.some((o) => o.is_upgrade) && c.options.some((o) => !o.is_upgrade) && (
                  <button onClick={() => setCmp(c)} className="h-8 px-3 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-slate-50" data-testid={`finish-compare-btn-${c.id}`}><Columns2 className="w-3.5 h-3.5" />Compare</button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {c.options.map((o) => {
                  const on = choices[c.id] === o.id;
                  return (
                    <button key={o.id} onClick={() => onPick(c, o)} className={`text-left rounded-2xl border p-2 transition-[border-color,transform] active:scale-[.97] ${on ? "border-slate-900 shadow-md" : "border-slate-200 hover:border-slate-400"}`} data-testid={`finish-option-${o.id}`}>
                      <span className="relative block aspect-square rounded-xl border" style={{ background: o.image_url ? `url(${asset(o.image_url)}) center/cover` : o.swatch }}>
                        {on && <Check className="absolute top-1.5 right-1.5 w-5 h-5 p-0.5 rounded-full bg-white text-slate-900" />}
                      </span>
                      <span className="block text-xs font-medium mt-2 leading-tight">{o.name}</span>
                      <span className={`block text-[11px] mt-0.5 ${o.is_upgrade ? "text-ba font-semibold" : "text-slate-500"}`}>{o.is_upgrade ? `+${money(o.price)}` : "Standard"}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        <div className="border-t p-6 bg-white space-y-1" data-testid="personalise-summary">
          <div className="flex justify-between text-sm text-slate-600"><span>Base price</span><span>{money(t.base)}</span></div>
          <div className="flex justify-between text-sm text-slate-600"><span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-ba" />Upgrades</span><span data-testid="personalise-upgrades-total">{money(t.upgrades)}</span></div>
          <TaxLines builder={builder} total={t.total} />
          <BrandButton className="w-full h-12 mt-3" onClick={onSave} data-testid="save-my-home-button">Save my home</BrandButton>
        </div>
      </SheetContent>
    </Sheet>
    {cmp && <FinishCompare cat={cmp} chosenId={choices[cmp.id]} onPick={onPick} onClose={() => setCmp(null)} />}
    </>
  );
}

export function SaveHomeDialog({ open, onOpenChange, houseTypeId, plotId, choices }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/selections", { house_type_id: houseTypeId, plot_id: plotId || "", choices, ...f, session_id: sessionId() });
      setRes(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setRes(null); }}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="save-home-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{res ? "Your home is saved" : "Save my home"}</DialogTitle>
          <DialogDescription>{res ? "We've emailed you a link and your branded PDF quote." : "Enter your details to receive a shareable link and PDF quote."}</DialogDescription>
        </DialogHeader>
        {res ? (
          <div className="space-y-3" data-testid="save-home-result">
            <p className="font-serif text-3xl text-bp">{money(res.total)}</p>
            <div className="flex gap-2">
              <Input readOnly value={res.share_url} data-testid="save-home-share-url" />
              <button className="w-10 h-10 rounded-md border grid place-items-center shrink-0" onClick={() => { navigator.clipboard?.writeText(res.share_url); toast.success("Link copied"); }} data-testid="save-home-copy"><Copy className="w-4 h-4" /></button>
            </div>
            <a href={res.pdf_url} target="_blank" rel="noreferrer" data-testid="save-home-pdf-link"><BrandButton className="w-full mt-2"><FileDown className="w-4 h-4" />Download PDF quote</BrandButton></a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Full name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="save-home-name" /></div>
            <div><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="save-home-email" /></div>
            <div><Label>Phone (optional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="save-home-phone" /></div>
            <BrandButton type="submit" className="w-full" disabled={busy} data-testid="save-home-submit">{busy ? "Saving…" : "Save & send my quote"}</BrandButton>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
